// PRO Portfolio Drift & Rebalancing Alpha Auditor -- feature orchestrator.
//
// Two-pane cockpit: the institutional input form on the left (sticky), and on
// the right either an empty prompt, a multi-engine processing state, or the
// full unlocked results dashboard. The endpoint is synchronous, so a single
// mutation drives the flow.

import React, { useMemo } from 'react';

import { useProDriftAudit } from './api';
import { CorridorFrontierChart } from './components/CorridorFrontierChart';
import { DrawdownRegimeChart } from './components/DrawdownRegimeChart';
import { ExecutiveBanner } from './components/ExecutiveBanner';
import { HoldingDiagnosticsTable } from './components/HoldingDiagnosticsTable';
import { LiveBlotterTable } from './components/LiveBlotterTable';
import { ProDriftAuditorForm } from './components/ProDriftAuditorForm';
import { StrategyComparisonTable } from './components/StrategyComparisonTable';
import { TradeLedgerTable } from './components/TradeLedgerTable';
import { WealthChart } from './components/WealthChart';
import { WeightDriftChart } from './components/WeightDriftChart';
import { useSymbolRoutes } from './useSymbolRoutes';
import type { ProDriftAuditRequest, ProDriftAuditResponse } from './types';

const ResultsView: React.FC<{ result: ProDriftAuditResponse }> = ({ result }) => {
    const tickers = useMemo(
        () => result.holding_diagnostics.map((h) => h.ticker),
        [result.holding_diagnostics],
    );

    // Every ticker across the holdings, live blotter and AI trade ledger -- so
    // each one can resolve to a clickable deep-dive link (opens in a new tab).
    const allSymbols = useMemo(() => {
        const set = new Set<string>();
        result.holding_diagnostics.forEach((h) => set.add(h.ticker));
        result.live_execution_blotter.forEach((o) => set.add(o.ticker));
        result.trade_ledger.forEach((e) => set.add(e.trigger_ticker));
        return Array.from(set);
    }, [result.holding_diagnostics, result.live_execution_blotter, result.trade_ledger]);

    const routes = useSymbolRoutes(allSymbols);

    return (
        <div className="space-y-6">
            <ExecutiveBanner banner={result.executive_banner} />

            <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/70 px-5 py-3 text-xs text-slate-500 dark:text-slate-400">
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                    {result.trading_days.toLocaleString('en-US')}
                </span>{' '}
                aligned trading days ({result.start_date} → {result.end_date}), {tickers.length} holdings.
            </div>

            <StrategyComparisonTable rows={result.strategy_comparison} />

            <WealthChart data={result.timeseries} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <CorridorFrontierChart data={result.corridor_frontier} />
                <DrawdownRegimeChart data={result.timeseries} />
            </div>

            <WeightDriftChart data={result.timeseries} tickers={tickers} />

            <LiveBlotterTable rows={result.live_execution_blotter} routes={routes} />

            <HoldingDiagnosticsTable rows={result.holding_diagnostics} routes={routes} />

            <TradeLedgerTable rows={result.trade_ledger} routes={routes} />
        </div>
    );
};

const ProcessingState: React.FC = () => (
    <div className="flex h-full min-h-[560px] items-center justify-center rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/70">
        <div className="flex flex-col items-center px-4 text-center">
            <div className="mb-4 h-11 w-11 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
            <span className="font-semibold text-slate-700 dark:text-slate-200">
                Running the institutional engine…
            </span>
            <span className="mt-1 max-w-sm text-sm text-slate-400">
                Six tax-aware backtests, a 27-policy Forge Ultra regime search and an 8-point corridor frontier —
                compiled to native code.
            </span>
        </div>
    </div>
);

const EmptyState: React.FC = () => (
    <div className="flex h-full min-h-[560px] items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40">
        <p className="max-w-md px-4 text-center text-lg font-medium text-slate-400">
            Enter your target allocation and tune the institutional knobs, then run the PRO audit.
        </p>
    </div>
);

export const ProDriftAuditorDashboard: React.FC = () => {
    const audit = useProDriftAudit();
    const result = audit.data ?? null;

    const handleSubmit = (payload: ProDriftAuditRequest) => {
        audit.mutate(payload);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4 h-fit lg:sticky lg:top-6">
                <ProDriftAuditorForm
                    onSubmit={handleSubmit}
                    isPending={audit.isPending}
                    error={audit.error ?? null}
                />
            </div>

            <div className="lg:col-span-8 flex flex-col">
                {audit.isPending ? (
                    <ProcessingState />
                ) : result ? (
                    <ResultsView result={result} />
                ) : (
                    <EmptyState />
                )}
            </div>
        </div>
    );
};
